import { fetchRaSupplierRoster } from '../../services/supplierRosterCache'
import { hideSimulatedSuppliers } from './simulatedSuppliers'
import { supabase } from '../../services/supabase'
import type { Supplier, SupplierMitigationTask, SupplierNews, SupplierSpecialReport } from '../../types'
import {
  resolveMitigationDisplayLocation,
  resolveNewsDisplayLocation,
} from '../../utils/regionToCoordinates'
import { cell } from './copyTableToClipboard'
import type { SupplierListFilters } from './earlySignalsDbIntent'
import {
  effectiveProcurementType,
  emptyMatchSentence,
  filterByCategoryScope,
  supplierMatchesGeoFilter,
} from './earlySignalsDbIntent'
import { matchesProcurementTypeFilter } from './esPortfolioFilters'
import { formatHighestScoreSentence, scoreBandWord, scoreValue } from './supplierRiskRatingsTable'
import {
  supplierBand,
  collapseToBestSupplierRowsForPortfolio,
  collapseSuppliersByNameAndProcurement,
  collapseSuppliersForPortfolio,
  bandCountsUnique,
  rollupCollapsedByAttribute,
  collapsedRegionKey,
  collapsedCategoryKey,
  collapsedBand,
  numericScore,
  supplierDisplayName,
  type CollapsedSupplierRisk,
} from './supplierRollup'
import type { GroupByDimension } from './riskAnalystSession'

export type { SupplierListFilters } from './earlySignalsDbIntent'

export const DB_TABLE_VISIBLE_CAP = 50

export type DbTableResult = {
  title: string
  headers: string[]
  rows: string[][]
  sourceLabel: string
  /** Optional link for the first cell of each row (Spend page). */
  rowHrefs?: Array<string | null>
  /** First-column click asks this question in the chat. Does not leave the page. */
  rowAsks?: Array<string | null>
  /** Signed-download storage path for downloadColumn (special report title). */
  downloadPaths?: Array<string | null>
  downloadNames?: Array<string | null>
  downloadColumn?: number
}

function asksForSupplierNames(names: Array<string | null | undefined>): Array<string | null> {
  return names.map((raw) => {
    const name = (raw || '').replace(/\s+/g, ' ').trim()
    if (!name || name === '—' || name === 'Unknown supplier') return null
    return `Tell me about ${name}`
  })
}

const ALERT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

/** Ensure overall_score is a finite number so banding matches Intelligence view (PostgREST quirks). */
function normalizeSupplierScores(list: Supplier[]): Supplier[] {
  return list.map((s) => {
    const score = numericScore(s.overall_score)
    return score == null ? s : { ...s, overall_score: score }
  })
}

function formatShortDate(raw: string | null | undefined): string {
  if (!raw) return '—'
  try {
    return new Date(raw).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return String(raw)
  }
}

function formatSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function contentTypeLabel(contentType: string | null | undefined): string {
  if (!contentType) return 'File'
  if (contentType.includes('pdf')) return 'PDF'
  if (contentType.includes('word') || contentType.includes('document')) return 'Word'
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'PowerPoint'
  return contentType
}

function bandLabel(band: 'low' | 'medium' | 'high' | null): string {
  if (band === 'high') return 'HIGH'
  if (band === 'medium') return 'MEDIUM'
  if (band === 'low') return 'LOW'
  return '—'
}

function isActiveMitigation(t: SupplierMitigationTask): boolean {
  return t.status !== 'closed' && t.status !== 'completed'
}

async function loadSupplierList(): Promise<Supplier[]> {
  try {
    return hideSimulatedSuppliers(await fetchRaSupplierRoster())
  } catch (e) {
    throw new Error(e instanceof Error ? e.message : 'Failed to load suppliers.')
  }
}

async function loadSupplierMap(): Promise<Map<string, Supplier>> {
  const list = await loadSupplierList()
  const map = new Map<string, Supplier>()
  for (const row of list) {
    map.set(row.id, row)
  }
  return map
}

export async function buildActiveMitigationsTable(): Promise<DbTableResult> {
  const [{ data, error }, supplierMap] = await Promise.all([
    supabase
      .from('supplier_mitigation_tasks')
      .select('*')
      .order('alert_date', { ascending: false })
      .order('created_at', { ascending: false }),
    loadSupplierMap(),
  ])
  if (error) throw new Error(error.message || 'Failed to load mitigations.')

  const tasks = ((data ?? []) as SupplierMitigationTask[])
    .filter(isActiveMitigation)
    .sort((a, b) => {
      const da = a.alert_date || a.created_at || ''
      const db = b.alert_date || b.created_at || ''
      return db.localeCompare(da)
    })

  const headers = ['Supplier', 'Task', 'Date', 'Location', 'Status']
  const rows = tasks.map((t) => {
    const supplier = t.supplier_id ? supplierMap.get(t.supplier_id) : undefined
    return [
      cell(supplier ? supplierDisplayName(supplier) : ''),
      cell(t.task),
      cell(formatShortDate(t.alert_date || t.created_at)),
      cell(resolveMitigationDisplayLocation(t, supplier ?? null)),
      cell(t.status),
    ]
  })

  return {
    title: 'Active mitigations',
    headers,
    rows,
    sourceLabel: 'Dashboard · Active mitigations',
    rowAsks: asksForSupplierNames(rows.map((r) => r[0])),
  }
}

export async function buildRecentAlertsTable(): Promise<DbTableResult> {
  const [{ data, error }, supplierMap] = await Promise.all([
    supabase.from('supplier_news').select('*').order('published_at', { ascending: false }),
    loadSupplierMap(),
  ])
  if (error) throw new Error(error.message || 'Failed to load alerts.')

  const cutoff = Date.now() - ALERT_WINDOW_MS
  const recent = ((data ?? []) as SupplierNews[])
    .filter((n) => {
      const d = n.published_at || n.created_at
      if (!d) return true
      return new Date(d).getTime() >= cutoff
    })
    .sort((a, b) => {
      const da = a.published_at || a.created_at || ''
      const db = b.published_at || b.created_at || ''
      return db.localeCompare(da)
    })

  const headers = ['Headline', 'Date', 'Location', 'Source', 'Supplier']
  const rows = recent.map((n) => {
    const supplier = n.supplier_id ? supplierMap.get(n.supplier_id) : undefined
    const sourceLabel =
      n.source === 'gnews'
        ? 'GNews'
        : n.source === 'master_database'
          ? 'Master DB'
          : n.source === 'excel'
            ? 'Excel'
            : n.source || ''
    return [
      cell(n.headline),
      cell(formatShortDate(n.published_at || n.created_at)),
      cell(resolveNewsDisplayLocation(n, supplier ?? null)),
      cell(sourceLabel),
      cell(supplier ? supplierDisplayName(supplier) : ''),
    ]
  })

  return {
    title: 'Recent alerts (7 days)',
    headers,
    rows,
    sourceLabel: 'Alerts',
  }
}

function rankedInScope(
  filters: SupplierListFilters
): Promise<Supplier[]> {
  return loadSupplierList().then((raw) => {
    const filtered = applyListFilters(normalizeSupplierScores(raw), filters)
    return collapseToBestSupplierRowsForPortfolio(filtered, effectiveProcurementType(filters))
      .filter((s) => numericScore(s.overall_score) != null)
      .sort(
        (a, b) =>
          (numericScore(b.overall_score) ?? -1) - (numericScore(a.overall_score) ?? -1)
      )
  })
}

function scopeFilters(
  categoryHint: string,
  procurementType?: 'direct' | 'indirect' | 'all',
  supplierIds?: ReadonlySet<string>
): SupplierListFilters {
  return { categoryHint, procurementType, supplierIds }
}

/** Top score in a category, labelled from the number (3.30 is Low). */
export async function buildHighestInCategoryText(
  categoryHint: string,
  procurementType?: 'direct' | 'indirect' | 'all',
  supplierIds?: ReadonlySet<string>
): Promise<string> {
  const ranked = await rankedInScope(scopeFilters(categoryHint, procurementType, supplierIds))
  const top = ranked[0]
  const score = top ? numericScore(top.overall_score) : null
  if (!top || score == null) {
    return emptyMatchSentence(`${categoryHint} (category or subcategory)`)
  }
  return formatHighestScoreSentence(top.name, categoryHint, score)
}

/** Portfolio next step for a role (“I am the chemicals manager”), using stored names only. */
export async function buildCategoryNextActionText(
  categoryHint: string,
  procurementType?: 'direct' | 'indirect' | 'all',
  supplierIds?: ReadonlySet<string>
): Promise<string> {
  const ranked = await rankedInScope(scopeFilters(categoryHint, procurementType, supplierIds))
  if (ranked.length === 0) {
    return emptyMatchSentence(`${categoryHint} (category or subcategory)`)
  }
  const lines = ranked.slice(0, 3).map((s) => {
    const score = numericScore(s.overall_score)!
    const word = scoreBandWord(score)
    const caveat = word === 'High' ? '' : ' That score is not high risk.'
    return `${s.name}: ${score.toFixed(2)} / 10 (${word}).${caveat}`
  })
  const topName = ranked[0].name
  return `For the ${categoryHint} portfolio, these are the highest scores in platform data:\n${lines.map((l) => `- ${l}`).join('\n')}\nNext question: “Tell me about ${topName}”.`
}

function scoreFilterLabel(filters: SupplierListFilters): string | null {
  if (filters.scoreFilter) {
    const { dim, op, value } = filters.scoreFilter
    return `${dim} ${op} ${value}`
  }
  if (filters.overallScoreMin != null) {
    const op = filters.overallScoreExclusive ? '>' : '>='
    return `score ${op} ${filters.overallScoreMin}`
  }
  return null
}

function procurementFilterLabel(filters: SupplierListFilters): string {
  const pt = effectiveProcurementType(filters)
  if (pt === 'all') return 'All'
  if (pt === 'indirect') return 'Indirect'
  return 'Direct'
}

function formatSpendK(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })
}

function listFilterBits(
  filters: SupplierListFilters,
  rows?: Array<{ category?: string | null; industry?: string | null; sub_category?: string | null }>
): string[] {
  const bits: string[] = [procurementFilterLabel(filters)]
  if (filters.riskBand) bits.push(`${filters.riskBand} risk`)
  if (filters.singleSource) bits.push('single-source')
  if (filters.subCategoryHint) bits.push(`${filters.subCategoryHint} subcategory`)
  else if (filters.categoryHint) {
    const q = filters.categoryHint.toLowerCase()
    const catHit =
      !rows ||
      rows.some((s) => (s.category || s.industry || '').toLowerCase().includes(q))
    if (rows && rows.length === 0) bits.push(`${filters.categoryHint} (category or subcategory)`)
    else if (rows && !catHit) bits.push(`${filters.categoryHint} subcategory`)
    else bits.push(filters.categoryHint)
  }
  if (filters.regionHint) bits.push(filters.regionHint)
  if (filters.nameHint) bits.push(`name ~ ${filters.nameHint}`)
  const scoreBit = scoreFilterLabel(filters)
  if (scoreBit) bits.push(scoreBit)
  return bits
}

export async function buildIntelligenceSuppliersTable(
  filters: SupplierListFilters = {}
): Promise<DbTableResult> {
  let list = applyListFilters(normalizeSupplierScores(await loadSupplierList()), filters)
  list = collapseToBestSupplierRowsForPortfolio(list, effectiveProcurementType(filters))

  const headers = [
    'Name',
    'Procurement',
    'Parent',
    'Location',
    'Region',
    'Category',
    'Spend $K',
    'Score',
    'Risk',
    'Fin',
    'Ops',
    'Legal',
    'SC',
  ]
  const rows = list.map((s) => [
    cell(supplierDisplayName(s)),
    cell(s.procurement_type === 'indirect' ? 'Indirect' : 'Direct'),
    cell(s.parent_name),
    cell(s.location),
    cell(s.kenvue_region),
    cell(s.category || s.industry),
    formatSpendK(s.spend_k),
    numericScore(s.overall_score) != null ? numericScore(s.overall_score)!.toFixed(1) : '—',
    cell(bandLabel(supplierBand(s))),
    cell(scoreValue(s, 'financial')),
    cell(scoreValue(s, 'operational')),
    cell(scoreValue(s, 'compliance')),
    cell(scoreValue(s, 'supply_chain')),
  ])

  const filterBits = listFilterBits(filters, list)

  return {
    title: `Intelligence view — ${filterBits.join(' · ')} (unique suppliers)`,
    headers,
    rows,
    sourceLabel: 'Intelligence view',
    rowAsks: asksForSupplierNames(rows.map((r) => r[0])),
  }
}

export async function buildMitigationTrackerTable(): Promise<DbTableResult> {
  const [{ data, error }, supplierMap] = await Promise.all([
    supabase
      .from('supplier_mitigation_tasks')
      .select('*')
      .order('created_at', { ascending: false }),
    loadSupplierMap(),
  ])
  if (error) throw new Error(error.message || 'Failed to load mitigation tracker.')

  const tasks = (data ?? []) as SupplierMitigationTask[]
  const headers = [
    'Supplier',
    'Task',
    'Date',
    'Category',
    'Sub-category',
    'Status',
    'Brand',
  ]
  const rows = tasks.map((t) => {
    const supplier = t.supplier_id ? supplierMap.get(t.supplier_id) : undefined
    return [
      cell(supplier ? supplierDisplayName(supplier) : ''),
      cell(t.task),
      cell(formatShortDate(t.alert_date || t.created_at)),
      cell(supplier?.category),
      cell(supplier?.sub_category),
      cell(t.status),
      cell(t.brand ?? supplier?.brand),
    ]
  })

  return {
    title: 'Mitigation tracker',
    headers,
    rows,
    sourceLabel: 'Mitigation tracker',
    rowAsks: asksForSupplierNames(rows.map((r) => r[0])),
  }
}

export async function buildSpecialReportsTable(options?: {
  supplierIds?: Set<string>
  supplierLabel?: string
}): Promise<DbTableResult> {
  const [{ data, error }, supplierMap] = await Promise.all([
    supabase
      .from('supplier_special_reports')
      .select('*')
      .order('created_at', { ascending: false }),
    loadSupplierMap(),
  ])
  if (error) throw new Error(error.message || 'Failed to load special reports.')

  let reports = (data ?? []) as SupplierSpecialReport[]
  if (options?.supplierIds) {
    reports = reports.filter((r) => options.supplierIds!.has(r.supplier_id))
  }
  const headers = ['Supplier', 'Title', 'Type', 'Size', 'Updated']
  const rows = reports.map((r) => {
    const supplier = supplierMap.get(r.supplier_id)
    return [
      cell(supplier ? supplierDisplayName(supplier) : 'Unknown supplier'),
      cell(r.file_name),
      cell(contentTypeLabel(r.content_type)),
      cell(formatSize(r.file_size)),
      cell(formatShortDate(r.created_at)),
    ]
  })

  const title = options?.supplierLabel
    ? `Special reports — ${options.supplierLabel}`
    : 'Special reports'

  return {
    title,
    headers,
    rows,
    sourceLabel: 'Special reports',
    rowAsks: asksForSupplierNames(rows.map((r) => r[0])),
    downloadPaths: reports.map((r) => r.file_path || null),
    downloadNames: reports.map((r) => r.file_name || null),
    downloadColumn: 1,
  }
}

export type BrandLineRow = {
  name: string
  brand: string | null
  category: string | null
  location: string | null
  overall_score: number | null
  procurement_type?: 'direct' | 'indirect' | null
  parent_name?: string | null
  spend_k?: number | null
  dm_em?: string | null
}

/**
 * One row per brand (product line) for a chosen supplier legal name.
 * Distinct brands sorted A→Z; empty brands omitted.
 */
export function buildSupplierBrandsTableFromRows(
  displayLabel: string,
  rows: BrandLineRow[]
): DbTableResult | null {
  const byBrand = new Map<string, BrandLineRow>()
  for (const row of rows) {
    const brand = (row.brand || '').trim()
    if (!brand) continue
    const key = brand.toLowerCase()
    const existing = byBrand.get(key)
    if (!existing) {
      byBrand.set(key, row)
      continue
    }
    const score = row.overall_score ?? -1
    const prev = existing.overall_score ?? -1
    if (score > prev) byBrand.set(key, row)
  }

  const sorted = [...byBrand.values()].sort((a, b) =>
    (a.brand || '').localeCompare(b.brand || '')
  )
  if (sorted.length === 0) return null

  return {
    title: `Brands — ${displayLabel}`,
    headers: ['Brand', 'Category', 'Location', 'Score'],
    rows: sorted.map((r) => [
      cell(r.brand),
      cell(r.category),
      cell(r.location),
      r.overall_score != null && Number.isFinite(r.overall_score)
        ? Number(r.overall_score).toFixed(1)
        : '—',
    ]),
    sourceLabel: 'Intelligence view · product lines',
  }
}

export async function buildBandCountsTable(
  filters: SupplierListFilters = {}
): Promise<DbTableResult> {
  const list = applyListFilters(normalizeSupplierScores(await loadSupplierList()), filters)
  const procurement = effectiveProcurementType(filters)
  const bands = bandCountsUnique(collapseSuppliersForPortfolio(list, procurement))
  const scope = procurementFilterLabel(filters)
  return {
    title: `Supplier risk band counts — ${scope}`,
    headers: ['Band', 'Count'],
    rows: [
      ['High', String(bands.high)],
      ['Medium', String(bands.medium)],
      ['Low', String(bands.low)],
      ['Unscored', String(bands.unscored)],
      ['Total', String(bands.total)],
    ],
    sourceLabel: 'Executive Summary · unique suppliers (highest score)',
  }
}

export async function buildUniqueSupplierTotalTable(
  filters: SupplierListFilters = {}
): Promise<DbTableResult> {
  const list = applyListFilters(normalizeSupplierScores(await loadSupplierList()), filters)
  const procurement = effectiveProcurementType(filters)
  const total = collapseSuppliersForPortfolio(list, procurement).length
  const scope = procurementFilterLabel(filters)
  return {
    title: `Unique suppliers — ${scope}`,
    headers: ['Metric', 'Count'],
    rows: [['Total', String(total)]],
    sourceLabel: 'Platform data · unique supplier names',
  }
}

export async function buildFilteredUniqueSupplierCountTable(
  filters: SupplierListFilters
): Promise<DbTableResult> {
  const filtered = applyListFilters(normalizeSupplierScores(await loadSupplierList()), filters)
  const procurement = effectiveProcurementType(filters)
  const unique = collapseSuppliersForPortfolio(filtered, procurement)
  const bits = listFilterBits(filters)
  return {
    title: `Unique suppliers — ${bits.join(' · ')}`,
    headers: ['Metric', 'Count'],
    rows: [['Total', String(unique.length)]],
    sourceLabel: 'Platform data · unique supplier names',
  }
}

export async function buildRegionCountsTable(
  filters: SupplierListFilters = {}
): Promise<DbTableResult> {
  const list = applyListFilters(normalizeSupplierScores(await loadSupplierList()), filters)
  const procurement = effectiveProcurementType(filters)
  const collapsed = collapseSuppliersForPortfolio(list, procurement)
  const rollup = rollupCollapsedByAttribute(collapsed, collapsedRegionKey)
  const rows = rollup.map((r) => [
    r.key,
    String(r.count),
    String(r.high),
    r.avg != null ? r.avg.toFixed(1) : '—',
  ])
  const scope = procurementFilterLabel(filters)

  return {
    title: `Suppliers by region — ${scope}`,
    headers: ['Region', 'Count', 'High', 'Avg score'],
    rows,
    sourceLabel: 'Intelligence view · unique suppliers (highest score)',
  }
}

function applyListFilters(list: Supplier[], filters: SupplierListFilters): Supplier[] {
  let filtered = filters.supplierIds
    ? list.filter((s) => s.id && filters.supplierIds!.has(s.id))
    : list
  const procurement = effectiveProcurementType(filters)
  if (procurement !== 'all') {
    filtered = filtered.filter((s) =>
      matchesProcurementTypeFilter(s.procurement_type, procurement)
    )
  }
  if (filters.riskBand) {
    filtered = filtered.filter((s) => supplierBand(s) === filters.riskBand)
  }
  if (filters.singleSource) {
    filtered = filtered.filter((s) => s.single_source === true)
  }
  if (filters.subCategoryHint || filters.categoryHint) {
    filtered = filterByCategoryScope(filtered, filters).rows
  }
  if (filters.regionHint || (filters.locationHints && filters.locationHints.length > 0)) {
    filtered = filtered.filter((s) => supplierMatchesGeoFilter(s, filters))
  }
  if (filters.nameHint) {
    const q = filters.nameHint.toLowerCase()
    filtered = filtered.filter((s) => (s.name || '').toLowerCase().includes(q))
  }
  if (filters.overallScoreMin != null) {
    const min = filters.overallScoreMin
    const exclusive = filters.overallScoreExclusive === true
    filtered = filtered.filter((s) => {
      const score = numericScore(s.overall_score)
      if (score == null) return false
      return exclusive ? score > min : score >= min
    })
  }
  if (filters.scoreFilter) {
    const { dim, op, value } = filters.scoreFilter
    filtered = filtered.filter((s) => {
      const n =
        dim === 'overall' ? numericScore(s.overall_score) : scoreValue(s, dim)
      if (n == null) return false
      if (op === '>') return n > value
      if (op === '>=') return n >= value
      if (op === '<') return n < value
      return n <= value
    })
  }
  return filtered
}

function collapsedDimKey(
  s: CollapsedSupplierRisk & { procurement_type?: string | null },
  dim: GroupByDimension
): string {
  if (dim === 'risk') {
    const band = collapsedBand(s)
    if (band === 'high') return 'High'
    if (band === 'medium') return 'Medium'
    if (band === 'low') return 'Low'
    return 'Unscored'
  }
  if (dim === 'region') return collapsedRegionKey(s) || 'Unclassified'
  if (dim === 'procurement') {
    return s.procurement_type === 'indirect' ? 'Indirect' : 'Direct'
  }
  return collapsedCategoryKey(s) || 'Unclassified'
}

/**
 * Grouped / multi-cut supplier counts (risk × region × category × procurement).
 */
export async function buildGroupedSuppliersTable(
  filters: SupplierListFilters = {},
  groupBy: GroupByDimension[] = ['region']
): Promise<DbTableResult> {
  const dims = (groupBy.length ? groupBy : (['region'] as GroupByDimension[])).slice(0, 3)
  const effectiveFilters: SupplierListFilters =
    dims.includes('procurement') && !filters.procurementType
      ? { ...filters, procurementType: 'all' }
      : filters
  const list = applyListFilters(normalizeSupplierScores(await loadSupplierList()), effectiveFilters)

  // Keep Direct vs Indirect as separate unique buckets (name vs parent).
  const procurement = effectiveProcurementType(effectiveFilters)
  const collapsed = dims.includes('procurement')
    ? collapseSuppliersByNameAndProcurement(list)
    : collapseSuppliersForPortfolio(list, procurement).map((s) => ({
        ...s,
        procurement_type: null as string | null,
      }))

  type Acc = { count: number; high: number; scoreSum: number; scoreN: number }
  const map = new Map<string, Acc>()

  for (const s of collapsed) {
    const parts = dims.map((d) => collapsedDimKey(s, d))
    const key = parts.join(' · ')
    const entry = map.get(key) ?? { count: 0, high: 0, scoreSum: 0, scoreN: 0 }
    entry.count += 1
    if (collapsedBand(s) === 'high') entry.high += 1
    if (s.overall_score != null && Number.isFinite(s.overall_score)) {
      entry.scoreSum += s.overall_score
      entry.scoreN += 1
    }
    map.set(key, entry)
  }

  const dimHeaders = dims.map((d) =>
    d === 'risk'
      ? 'Risk'
      : d === 'region'
        ? 'Region'
        : d === 'procurement'
          ? 'Procurement'
          : 'Category'
  )
  const rows = [...map.entries()]
    .sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]))
    .map(([key, acc]) => {
      const parts = key.split(' · ')
      return [
        ...dims.map((_, i) => parts[i] || '—'),
        String(acc.count),
        String(acc.high),
        acc.scoreN > 0 ? (acc.scoreSum / acc.scoreN).toFixed(1) : '—',
      ]
    })

  const filterBits = listFilterBits(effectiveFilters)

  const byLabel = dims.join(' · ')
  return {
    title: `Suppliers by ${byLabel} — ${filterBits.join(' · ')}`,
    headers: [...dimHeaders, 'Count', 'High', 'Avg score'],
    rows,
    sourceLabel: 'Platform data · unique suppliers (grouped)',
  }
}

export async function buildMitigationsForSupplierTable(
  displayLabel: string,
  supplierIds: Set<string>
): Promise<DbTableResult> {
  const [{ data, error }, supplierMap] = await Promise.all([
    supabase
      .from('supplier_mitigation_tasks')
      .select('*')
      .order('alert_date', { ascending: false })
      .order('created_at', { ascending: false }),
    loadSupplierMap(),
  ])
  if (error) throw new Error(error.message || 'Failed to load mitigations.')

  const tasks = ((data ?? []) as SupplierMitigationTask[]).filter(
    (t) => t.supplier_id && supplierIds.has(t.supplier_id)
  )

  const headers = ['Supplier', 'Task', 'Date', 'Location', 'Status', 'Brand']
  const rows = tasks.map((t) => {
    const supplier = t.supplier_id ? supplierMap.get(t.supplier_id) : undefined
    return [
      cell(supplier ? supplierDisplayName(supplier) : ''),
      cell(t.task),
      cell(formatShortDate(t.alert_date || t.created_at)),
      cell(resolveMitigationDisplayLocation(t, supplier ?? null)),
      cell(t.status),
      cell(t.brand ?? supplier?.brand),
    ]
  })

  return {
    title: `Mitigations — ${displayLabel}`,
    headers,
    rows,
    sourceLabel: 'Mitigation tracker · supplier filter',
    rowAsks: asksForSupplierNames(rows.map((r) => r[0])),
  }
}

export function buildSupplierSummaryTable(
  displayLabel: string,
  rows: BrandLineRow[]
): DbTableResult {
  const brandsTable = buildSupplierBrandsTableFromRows(displayLabel, rows)
  if (brandsTable) {
    return {
      ...brandsTable,
      title: `Supplier summary — ${displayLabel}`,
      sourceLabel: 'Intelligence view · summary',
    }
  }
  const first = rows[0]
  return {
    title: `Supplier summary — ${displayLabel}`,
    headers: ['Field', 'Value'],
    rows: [
      ['Name', cell(displayLabel)],
      [
        'Procurement',
        cell(first?.procurement_type === 'indirect' ? 'Indirect' : first ? 'Direct' : null),
      ],
      ['Parent', cell(first?.parent_name)],
      ['Category', cell(first?.category)],
      ['Location', cell(first?.location)],
      ['Spend $K', formatSpendK(first?.spend_k)],
      ['DM/EM', cell(first?.dm_em)],
      ['Score', first?.overall_score != null ? Number(first.overall_score).toFixed(1) : '—'],
      ['Brands', '—'],
    ],
    sourceLabel: 'Intelligence view · summary',
  }
}
